import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Browser } from '@capacitor/browser';
import { Typography } from '@mui/material';
import classNames from 'classnames';
import {
	getRadabankCurrentCardSelector,
	getRadabankInternalActiveCardsListSelector,
	getUserProfileSelector,
	setAppLoading,
	showToast,
	useAppDispatch,
	useAppSelector,
} from 'store';
import { Navbar } from 'components/Navbar';
import { RadabankCurrentCardPreview, RadabankSectionHeader } from 'components/RadabankCard';
import { CustomButton } from 'components/shared';
import { NumberInput } from 'components/shared/NumberInput';
import {
	FIREBASE_EVENT_ANALYTICS_BUTTON,
	FIREBASE_EVENT_ANALYTICS_PAGE,
	RADABANK_MAX_ALLOWED_TRANSACTION_AMOUNT,
} from 'utils/constants';
import { ROUTER_URL } from 'utils/enums';
import { convertUAHToCoins, getErrorMessage } from 'utils/helpers';
import { useGoBackRadabank } from 'utils/hooks';
import { FirebaseAnalytics, RadabankService } from 'utils/services';
import { IUserProfileResponse } from 'utils/types';
import { radabankTopUpValidationSchema } from 'utils/validation';
import styles from './index.module.scss';

export const RadabankCardOperationTopUpPage: FC = () => {
	const handleClickBack = useGoBackRadabank();

	const dispatch = useAppDispatch();
	const history = useHistory();

	const currentCard = useAppSelector(getRadabankCurrentCardSelector);
	const profile = useAppSelector(getUserProfileSelector);
	const activeCardsList = useAppSelector(getRadabankInternalActiveCardsListSelector);

	const [amount, setAmount] = useState('');
	const [isValid, setIsValid] = useState(false);
	const [isTouched, setIsTouched] = useState(false);

	const handleChangeAmountValue = async (value: string): Promise<void> => {
		setAmount(value);

		try {
			await radabankTopUpValidationSchema.validate(value);
			setIsValid(true);
		} catch (error) {
			setIsValid(false);
		}
	};

	const handleBlurInput = () => {
		setIsTouched(true);
	};

	const handleClickCardTopUp = async () => {
		try {
			FirebaseAnalytics.logEvent(
				FIREBASE_EVENT_ANALYTICS_PAGE.RADABANK.CARD_TOP_UP,
				FIREBASE_EVENT_ANALYTICS_BUTTON.NEXT
			);
			if (currentCard) {
				dispatch(setAppLoading(true));
				const response = await RadabankService.createP2PData({
					userId: (profile as IUserProfileResponse).userId,
					cardid: currentCard.id,
					amount: `${convertUAHToCoins(amount)}`,
				});
				await Browser.open({ url: response.data.url });
			}
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		} finally {
			dispatch(setAppLoading(false));
		}
	};

	const handleTransferBetweenOwnCards = () => {
		FirebaseAnalytics.logEvent(
			FIREBASE_EVENT_ANALYTICS_PAGE.RADABANK.CARD_TOP_UP,
			FIREBASE_EVENT_ANALYTICS_BUTTON.CARD_TOP_UP_FROM_OWN
		);
		history.push(ROUTER_URL.RADABANK_CARD_TRANSFER_BETWEEN_OWN_CARDS);
	};

	const handleGoBack = () => {
		FirebaseAnalytics.logEvent(
			FIREBASE_EVENT_ANALYTICS_PAGE.RADABANK.CARD_TOP_UP,
			FIREBASE_EVENT_ANALYTICS_BUTTON.GO_BACK
		);
		handleClickBack();
	};

	if (!currentCard) return <Navbar />;
	return (
		<div className={classNames('page', styles.page)}>
			<RadabankSectionHeader title="Поповнити свою картку" onClickBack={handleGoBack} />
			<div className={styles.content}>
				<Typography variant="caption" color="var(--color-neutral-600)" marginBottom="var(--spacing-1)">
					Отримувач
				</Typography>
				<RadabankCurrentCardPreview wrapperClassName={styles.content__card} />

				<NumberInput
					label="Сума"
					value={amount}
					onChange={handleChangeAmountValue}
					onBlur={handleBlurInput}
					error={!isValid && isTouched}
					max={RADABANK_MAX_ALLOWED_TRANSACTION_AMOUNT}
				/>
			</div>
			<div className={styles.actions}>
				<CustomButton
					disabled={activeCardsList.length <= 1}
					fill="outline"
					onClick={handleTransferBetweenOwnCards}
					label="Поповнити зі своїх карток"
				/>
				<CustomButton onClick={handleClickCardTopUp} label="Далі" disabled={!convertUAHToCoins(amount) || !isValid} />
			</div>
		</div>
	);
};
